import {noop} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {EventHandler, FrontEvent} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {previewText} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {PaletteColor} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {ExpressionDryRunViewer} from './expressionDryRunViewer';
import {getExpressionTypeIcon} from './expressionIcons';
import {getLabelForInferredType} from './expressionLabels';
import {ExpressionDryRunSnapshot} from './expressionModel';
import {ResultTypesEnum} from './infer/internal';

/*
 * Constants.
 */

const MAX_TOOLTIP_PREVIEW = 600;

/*
 * Props.
 */

interface ExpressionDryRunPillProps {
  title: ReactNode;
  dryRun?: ExpressionDryRunSnapshot;
}

/*
 * Styles.
 */

const StyledDryRunPillButton = styled(ChromelessButton)<{$color: PaletteColor}>`
  color: ${(p) => p.$color.shade40};

  &:hover {
    color: ${(p) => p.$color.shade50};
  }
`;

const StyledHeader = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;

  font-weight: ${fontWeights.bold};
`;

const StyledTypeIcon = styled(Icon)`
  display: inline-block;
  margin-right: 4px;
`;

const StyledContent = styled.div<{$wordBreak: string; $whiteSpace: string}>`
  word-break: ${(p) => p.$wordBreak};
  white-space: ${(p) => p.$whiteSpace};
`;

/*
 * Component.
 */

export const ExpressionDryRunPill: FC<ExpressionDryRunPillProps> = ({dryRun, title}) => {
  const [isInspectorOpen, toggleOpen] = useToggle();

  if (!dryRun) {
    return null;
  }

  const onOpenInspector = (event: FrontEvent) => {
    event.stopPropagation();
    toggleOpen();
  };

  const inferredType = {type: dryRun.type};
  const icon = getExpressionTypeIcon(inferredType).regular.filled;

  // JSON payloads tend to be long so don't attempt to respect word breaks.
  const shouldBreakWords = dryRun.type === ResultTypesEnum.JSON;

  const shouldPreWrap = dryRun.type === ResultTypesEnum.LIST;

  const isUndefinedResult = dryRun.type === ResultTypesEnum.UNDEFINED;

  const preview = previewText(dryRun.value, MAX_TOOLTIP_PREVIEW);
  const typeName = getLabelForInferredType(inferredType);

  const renderTooltip = () => (
    <div>
      <StyledHeader>
        <StyledTypeIcon name={icon} />
        <span>{typeName}</span>
      </StyledHeader>
      <StyledContent
        $whiteSpace={shouldPreWrap ? 'pre-wrap' : 'normal'}
        $wordBreak={shouldBreakWords ? 'break-all' : 'normal'}
      >
        {preview}
      </StyledContent>
    </div>
  );

  return (
    <>
      <TooltipCoordinator render={renderTooltip} placement="bottom">
        <DryRunPill hasError={isUndefinedResult} onClick={onOpenInspector} />
      </TooltipCoordinator>
      {isInspectorOpen && (
        <ExpressionDryRunViewer dryRun={dryRun} title={title} onRequestClose={toggleOpen} />
      )}
    </>
  );
};

const DryRunPill: FC<{hasError?: boolean; onClick?: EventHandler}> = ({hasError, onClick}) => {
  const theme = useUserTheme();

  return (
    <StyledDryRunPillButton
      $color={hasError ? theme.palette.red : theme.palette.pink}
      onClick={onClick || noop}
    >
      <Icon name={hasError ? 'warningFilledTriangle' : 'unreadSmall'} />
    </StyledDryRunPillButton>
  );
};
